import React, { useState, useEffect } from "react";
import Form, { Item, GroupItem } from "devextreme-react/form";
import notify from "devextreme/ui/notify";
import { Button } from "devextreme-react/button";
import { useDispatch } from "react-redux";
import { getResourceAPI } from "../redux/slices/resourceSlice";
import { pendingCall } from "../utils/api";
import Loader from "../components/generic/Loader";
import "../styles/styles.css";
import useResources from "../hooks/useResources";
import useResourceStatuses from "../hooks/useResourceStatus.jsx";
import { Grid } from "@material-ui/core";
import InvoicesGrid from "../components/vendorReports/InvoicesGrid";
import CustomSelection from "../components/generic/CustomSelection";

const createQuery = (formData, vendors) => {
  if (formData.vendor) {
    const foundVendor = vendors.find(
      (vendor) => vendor.name === formData.vendor
    );
    if (foundVendor) return { ...formData, vendor_id: foundVendor.id };
  }

  return formData;
};

const SuppliersPage = () => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({});

  const { currencies, vendors, vendorInvoices } = useResources();

  const {
    vendorStatus,
    currencyStatus,
    employeeStatus,
    vendorInvoiceSearchStatus,
  } = useResourceStatuses();

  useEffect(() => {
    pendingCall(vendorStatus) && dispatch(getResourceAPI({ path: "vendor" }));
    pendingCall(currencyStatus) &&
      dispatch(getResourceAPI({ path: "currency" }));
    pendingCall(employeeStatus) &&
      dispatch(getResourceAPI({ path: "employee" }));
  }, [vendorStatus, currencyStatus, employeeStatus, dispatch]);

  const handleSearch = () => {
    dispatch(
      getResourceAPI({
        path: "vendor-invoice-search",
        query: createQuery(formData, vendors),
      })
    ).then(({ payload }) => {
      if (payload.error) {
        notify(payload.error, "error", 500);
      }
    });
  };

  const clearFormData = () => {
    notify("Cleared Form", "success", 500);
    return setFormData({});
  };

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
        position: "relative",
      }}
    >
      {(vendorStatus === "pending" ||
        vendorInvoiceSearchStatus === "pending") && <Loader />}
      <Form
        onFieldDataChanged={(e) => {
          const { dataField, value } = e;
          if (!dataField || value === undefined) return;

          return setFormData((prevFormData) => ({
            ...prevFormData,
            [dataField]: value,
          }));
        }}
        labelLocation="top"
        onContentReady={(e) => e.component.validate()}
        id="form"
      >
        <GroupItem caption="Invoices Filters" colCount={8}>
          <Item dataField="vendor">
            <CustomSelection
              valueExpr="name"
              displayExpr="name"
              value={formData.vendor}
              resourceName="vendors"
              resourcePath="vendor"
              handleChange={(e) =>
                setFormData((prev) => ({ ...prev, vendor: e.value }))
              }
              disabled={vendorStatus !== "done"}
            />
          </Item>
          <Item
            dataField="from_amount"
            editorType="dxNumberBox"
            editorOptions={{
              width: "100%",
              value: formData.from_amount,
            }}
          />
          <Item
            dataField="to_amount"
            editorType="dxNumberBox"
            editorOptions={{
              width: "100%",
              value: formData.to_amount,
            }}
          />

          <Item
            dataField="currencies"
            editorType="dxSelectBox"
            editorOptions={{
              items: Object.values(currencies || {}).map(
                (currency) => currency.name
              ),
              searchEnabled: true,
              value: formData.currencies,
              disabled: vendorStatus !== "done",
            }}
          />
          <Item
            dataField="payment_type"
            editorType="dxSelectBox"
            editorOptions={{
              items: ["Wire Transfer", "Credit Note", "Check", "Cash"],
              searchEnabled: true,
              value: formData.payment_type,
            }}
          />
          <Item
            editorType="dxNumberBox"
            dataField="due_days"
            editorOptions={{
              value: formData.due_days,
            }}
          />
          <Item
            dataField="vendor_ref"
            editorOptions={{
              inputAttr: { autocomplete: "vendor_ref" },
            }}
          />
          <Item
            dataField="our_ref"
            editorOptions={{
              inputAttr: { autocomplete: "our_ref" },
              value: formData.our_ref,
            }}
          />
          <Item
            dataField="description"
            editorOptions={{
              inputAttr: { autocomplete: "description" },
            }}
          />
        </GroupItem>
      </Form>
      <Grid
        container
        style={{ margin: "16px" }}
        align="center"
        justifyContent="center"
        spacing={5}
      >
        <Grid item xs={6}>
          <Button
            icon="clear"
            type="normal"
            text="Clear"
            hint="Clear Form"
            width="200"
            onClick={clearFormData}
          />
        </Grid>
        <Grid item xs={6}>
          <Button
            icon="search"
            type="success"
            text="Search"
            hint="Search trade-cases"
            width="200"
            onClick={handleSearch}
          />
        </Grid>
      </Grid>
      <InvoicesGrid dataSource={vendorInvoices} />
    </div>
  );
};

export default SuppliersPage;
